<template>
  <div class="portfolio-hub">
    <header class="hub-header">
      <div>
        <h1>Portfolio</h1>
        <p class="hub-subtitle">Clients and projects — find what you need, then go work it.</p>
      </div>
    </header>

    <div class="p-bento p-bento--diagonal">
      <!-- A wide: Clients -->
      <section class="p-bento-cell clients-cell" ref="clientsCell">
        <header class="cell-header">
          <div>
            <h2>Clients</h2>
            <p class="cell-sub">{{ clientCountLabel }}</p>
          </div>
          <div class="cell-header-actions">
            <router-link to="/clients/all" class="view-all-link">View all</router-link>
            <button type="button" class="btn btn-primary btn-sm" @click="openClientModal()">
              <i class="fas fa-plus"></i> Add
            </button>
          </div>
        </header>

        <div class="toolbar">
          <div class="search-wrap">
            <i class="fas fa-search"></i>
            <input
              v-model="clientQuery"
              type="search"
              placeholder="Search clients…"
              aria-label="Search clients"
            />
          </div>
          <label class="archive-toggle">
            <input v-model="showArchivedClients" type="checkbox" />
            <span>Archived</span>
          </label>
        </div>

        <div v-if="loadingClients" class="cell-state">Loading clients…</div>
        <div v-else-if="clientsError" class="cell-state error">{{ clientsError }}</div>
        <div v-else-if="visibleClients.length === 0" class="cell-state">
          No clients match. <button type="button" class="linkish" @click="openClientModal()">Add a client</button>
        </div>
        <ul v-else class="entity-list">
          <li v-for="client in previewClients" :key="client.id" class="entity-row" :class="{ muted: client.archived }">
            <div class="entity-main">
              <button type="button" class="entity-title linkish" @click="editClient(client)">
                {{ client.name }}
              </button>
              <div class="entity-meta">
                <span v-if="client.industry_type_display" class="pill">{{ client.industry_type_display }}</span>
                <span class="pill" :class="client.archived ? 'pill-muted' : 'pill-ok'">
                  {{ client.archived ? 'Archived' : 'Active' }}
                </span>
              </div>
            </div>
            <div class="entity-actions">
              <router-link
                :to="`/clients/${client.id}/workflow-settings`"
                class="btn btn-ghost btn-sm"
                title="Workflow settings"
              >
                <i class="fas fa-cogs"></i>
              </router-link>
            </div>
          </li>
        </ul>
        <p v-if="clientsOverflow" class="preview-overflow">
          Showing {{ previewClients.length }} of {{ visibleClients.length }}.
          <router-link to="/clients/all">View all clients</router-link>
        </p>
      </section>

      <!-- B narrow: Clients pulse -->
      <section class="p-bento-cell pulse-cell">
        <h2>Client pulse</h2>
        <div class="pulse-stat">
          <span class="pulse-value">{{ activeClientCount }}</span>
          <span class="pulse-label">Active</span>
        </div>
        <div class="pulse-stat">
          <span class="pulse-value">{{ archivedClientCount }}</span>
          <span class="pulse-label">Archived</span>
        </div>
        <p class="pulse-hint">Open a client to edit details or configure workflows.</p>
      </section>

      <!-- C narrow: Projects pulse -->
      <section class="p-bento-cell pulse-cell" :class="{ 'pulse-warn': projectsMissingCheck > 0 }">
        <h2>Project pulse</h2>
        <div class="pulse-stat">
          <span class="pulse-value">{{ activeProjectCount }}</span>
          <span class="pulse-label">Active</span>
        </div>
        <div class="pulse-stat" :class="{ warn: projectsMissingCheck > 0 }">
          <span class="pulse-value">{{ projectsMissingCheck }}</span>
          <span class="pulse-label">No check assigned</span>
        </div>
        <button type="button" class="btn btn-primary btn-block" @click="openProjectModal()">
          <i class="fas fa-plus"></i> New project
        </button>
        <p class="pulse-hint">Filter the list for “Needs check” when something looks off.</p>
      </section>

      <!-- D wide: Projects -->
      <section class="p-bento-cell projects-cell" ref="projectsCell">
        <header class="cell-header">
          <div>
            <h2>Projects</h2>
            <p class="cell-sub">{{ projectCountLabel }}</p>
          </div>
          <div class="cell-header-actions">
            <router-link to="/projects" class="view-all-link">View all</router-link>
            <button type="button" class="btn btn-primary btn-sm" @click="openProjectModal()">
              <i class="fas fa-plus"></i> Create
            </button>
          </div>
        </header>

        <div class="toolbar">
          <div class="search-wrap">
            <i class="fas fa-search"></i>
            <input
              v-model="projectQuery"
              type="search"
              placeholder="Search by name, client, description…"
              aria-label="Search projects"
            />
          </div>
          <select v-model="projectFilter" class="filter-select" aria-label="Filter projects">
            <option value="all">All</option>
            <option value="needs_check">Needs check</option>
            <option value="has_check">Has check</option>
          </select>
          <label class="archive-toggle">
            <input v-model="showArchivedProjects" type="checkbox" @change="fetchProjects" />
            <span>Archived</span>
          </label>
        </div>

        <div v-if="loadingProjects" class="cell-state">Loading projects…</div>
        <div v-else-if="projectsError" class="cell-state error">{{ projectsError }}</div>
        <div v-else-if="visibleProjects.length === 0" class="cell-state">
          No projects match.
          <button type="button" class="linkish" @click="openProjectModal()">Create a project</button>
        </div>
        <ul v-else class="entity-list">
          <li
            v-for="project in previewProjects"
            :key="project.id"
            class="entity-row project-row"
            :class="{ muted: project.archived }"
          >
            <div class="entity-main">
              <router-link :to="`/projects/${project.id}`" class="entity-title">
                {{ project.name }}
              </router-link>
              <p v-if="project.description" class="entity-desc">{{ truncate(project.description, 120) }}</p>
              <div class="entity-meta">
                <span v-if="clientName(project)" class="pill">{{ clientName(project) }}</span>
                <span
                  class="pill"
                  :class="project.default_comprehensive_check_name ? 'pill-ok' : 'pill-warn'"
                >
                  <i :class="project.default_comprehensive_check_name ? 'fas fa-clipboard-check' : 'fas fa-exclamation-triangle'"></i>
                  {{ project.default_comprehensive_check_name || 'No check assigned' }}
                </span>
                <span v-if="project.archived" class="pill pill-muted">Archived</span>
              </div>
            </div>
            <div class="entity-actions">
              <router-link :to="`/projects/${project.id}`" class="btn btn-primary btn-sm">
                Open
              </router-link>
              <router-link :to="`/projects/${project.id}/settings`" class="btn btn-ghost btn-sm" title="Settings">
                <i class="fas fa-cogs"></i>
              </router-link>
            </div>
          </li>
        </ul>
        <p v-if="projectsOverflow" class="preview-overflow">
          Showing {{ previewProjects.length }} of {{ visibleProjects.length }}.
          <router-link to="/projects">View all projects</router-link>
        </p>
      </section>
    </div>

    <!-- Client modal -->
    <div v-if="showClientModal" class="modal-overlay" @click.self="closeClientModal">
      <div class="modal-content">
        <header class="modal-header">
          <h3>{{ editingClient ? 'Edit client' : 'Add client' }}</h3>
          <button type="button" class="close-btn" @click="closeClientModal"><i class="fas fa-times"></i></button>
        </header>
        <form class="modal-form" @submit.prevent="saveClient">
          <label>Name *<input v-model="clientForm.name" required maxlength="255" /></label>
          <label>Email *<input v-model="clientForm.email" type="email" required /></label>
          <label>Phone<input v-model="clientForm.phone" /></label>
          <label>
            Industry
            <select v-model="clientForm.industry_type">
              <option value="INSURANCE">Insurance</option>
              <option value="CONSTRUCTION">Construction</option>
              <option value="REAL_ESTATE">Real Estate</option>
              <option value="TECHNOLOGY">Technology</option>
              <option value="HEALTHCARE">Healthcare</option>
              <option value="OTHER">Other</option>
            </select>
          </label>
          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" @click="closeClientModal">Cancel</button>
            <button type="submit" class="btn btn-primary">{{ editingClient ? 'Save' : 'Create' }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Project modal -->
    <div v-if="showProjectModal" class="modal-overlay" @click.self="closeProjectModal">
      <div class="modal-content">
        <header class="modal-header">
          <h3>Create project</h3>
          <button type="button" class="close-btn" @click="closeProjectModal"><i class="fas fa-times"></i></button>
        </header>
        <form class="modal-form" @submit.prevent="createProject">
          <label>Name *<input v-model="projectForm.name" required maxlength="255" /></label>
          <label>
            What will you analyze? *
            <textarea v-model="projectForm.description" rows="3" required maxlength="1000"></textarea>
          </label>
          <label>
            Client *
            <select v-model="projectForm.client" required>
              <option disabled value="">Select a client…</option>
              <option v-for="c in activeClientsForSelect" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </label>
          <p class="form-hint">Next: open Settings on the project to assign the compliance check.</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" @click="closeProjectModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="!projectFormValid">Create &amp; open</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import api from '@/services/api';
import { UI_CONSTANTS } from '@/constants';

/** Hub bento preview cap — full set lives on View all pages (hub-view-all rule). */
const HUB_PREVIEW_LIMIT = 8;

function asList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.projects)) return payload.projects;
  if (Array.isArray(payload?.clients)) return payload.clients;
  return [];
}

export default {
  name: 'PortfolioHub',
  data() {
    return {
      clients: [],
      projects: [],
      loadingClients: true,
      loadingProjects: true,
      clientsError: null,
      projectsError: null,
      clientQuery: '',
      projectQuery: '',
      debouncedClientQuery: '',
      debouncedProjectQuery: '',
      showArchivedClients: false,
      showArchivedProjects: false,
      projectFilter: 'all',
      showClientModal: false,
      showProjectModal: false,
      editingClient: null,
      clientForm: {
        name: '',
        email: '',
        phone: '',
        industry_type: 'INSURANCE',
        business_type: 'CORPORATION',
        description: '',
        archived: false,
      },
      projectForm: {
        name: '',
        description: '',
        client: '',
      },
      clientSearchDebounce: null,
      projectSearchDebounce: null,
    };
  },
  computed: {
    activeClientCount() {
      return this.clients.filter((c) => !c.archived).length;
    },
    archivedClientCount() {
      return this.clients.filter((c) => c.archived).length;
    },
    activeProjectCount() {
      return this.projects.filter((p) => !p.archived).length;
    },
    projectsMissingCheck() {
      return this.projects.filter((p) => !p.archived && !p.default_comprehensive_check_name).length;
    },
    clientCountLabel() {
      const n = this.visibleClients.length;
      return `${n} shown · ${this.activeClientCount} active`;
    },
    projectCountLabel() {
      const n = this.visibleProjects.length;
      return `${n} shown · ${this.activeProjectCount} active`;
    },
    activeClientsForSelect() {
      return this.clients.filter((c) => !c.archived);
    },
    visibleClients() {
      let list = this.showArchivedClients
        ? this.clients
        : this.clients.filter((c) => !c.archived);
      const q = this.debouncedClientQuery.trim().toLowerCase();
      if (q) {
        list = list.filter((c) => {
          const hay = [c.name, c.email, c.phone, c.industry_type_display, c.description]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return hay.includes(q);
        });
      }
      return list;
    },
    visibleProjects() {
      let list = this.showArchivedProjects
        ? this.projects
        : this.projects.filter((p) => !p.archived);
      if (this.projectFilter === 'needs_check') {
        list = list.filter((p) => !p.default_comprehensive_check_name);
      } else if (this.projectFilter === 'has_check') {
        list = list.filter((p) => !!p.default_comprehensive_check_name);
      }
      const q = this.debouncedProjectQuery.trim().toLowerCase();
      if (q) {
        list = list.filter((p) => {
          const hay = [p.name, p.description, this.clientName(p)]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return hay.includes(q);
        });
      }
      return list;
    },
    previewClients() {
      return this.visibleClients.slice(0, HUB_PREVIEW_LIMIT);
    },
    previewProjects() {
      return this.visibleProjects.slice(0, HUB_PREVIEW_LIMIT);
    },
    clientsOverflow() {
      return this.visibleClients.length > HUB_PREVIEW_LIMIT;
    },
    projectsOverflow() {
      return this.visibleProjects.length > HUB_PREVIEW_LIMIT;
    },
    projectFormValid() {
      return (
        this.projectForm.name.trim()
        && this.projectForm.description.trim()
        && this.projectForm.client
      );
    },
  },
  watch: {
    clientQuery() {
      this.clientSearchDebounce();
    },
    projectQuery() {
      this.projectSearchDebounce();
    },
    '$route.query.focus': {
      immediate: true,
      handler(focus) {
        if (focus === 'projects') {
          this.$nextTick(() => this.scrollToProjects());
        }
      },
    },
  },
  created() {
    this.clientSearchDebounce = debounce(() => {
      this.debouncedClientQuery = this.clientQuery;
    }, UI_CONSTANTS.SEARCH_DEBOUNCE_MS || 300);
    this.projectSearchDebounce = debounce(() => {
      this.debouncedProjectQuery = this.projectQuery;
    }, UI_CONSTANTS.SEARCH_DEBOUNCE_MS || 300);
    this.fetchClients();
    this.fetchProjects();
  },
  methods: {
    truncate(text, n) {
      if (!text) return '';
      return text.length > n ? `${text.slice(0, n)}…` : text;
    },
    clientName(project) {
      if (project.client_name) return project.client_name;
      const id = project.client?.id || project.client;
      if (!id) return '';
      const c = this.clients.find((x) => x.id === id);
      return c ? c.name : '';
    },
    scrollToProjects() {
      this.$refs.projectsCell?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    async fetchClients() {
      this.loadingClients = true;
      this.clientsError = null;
      try {
        const response = await api.get('/clients/');
        this.clients = asList(response.data);
      } catch (err) {
        this.clientsError = err.message || 'Failed to load clients';
      } finally {
        this.loadingClients = false;
      }
    },
    async fetchProjects() {
      this.loadingProjects = true;
      this.projectsError = null;
      try {
        const params = this.showArchivedProjects ? { show_archived: 'true' } : {};
        const response = await api.get('/projects/', { params });
        this.projects = asList(response.data);
      } catch (err) {
        this.projectsError = err.message || 'Failed to load projects';
      } finally {
        this.loadingProjects = false;
      }
    },
    openClientModal() {
      this.editingClient = null;
      this.clientForm = {
        name: '',
        email: '',
        phone: '',
        industry_type: 'INSURANCE',
        business_type: 'CORPORATION',
        description: '',
        archived: false,
      };
      this.showClientModal = true;
    },
    editClient(client) {
      this.editingClient = client;
      this.clientForm = {
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        industry_type: client.industry_type || 'OTHER',
        business_type: client.business_type || 'CORPORATION',
        description: client.description || '',
        archived: !!client.archived,
      };
      this.showClientModal = true;
    },
    closeClientModal() {
      this.showClientModal = false;
      this.editingClient = null;
    },
    async saveClient() {
      try {
        if (this.editingClient) {
          await api.put(`/clients/${this.editingClient.id}/`, this.clientForm);
        } else {
          await api.post('/clients/', this.clientForm);
        }
        await this.fetchClients();
        this.closeClientModal();
      } catch (err) {
        this.clientsError = err.response?.data?.detail || err.message || 'Save failed';
      }
    },
    openProjectModal() {
      this.projectForm = { name: '', description: '', client: '' };
      this.showProjectModal = true;
    },
    closeProjectModal() {
      this.showProjectModal = false;
    },
    async createProject() {
      if (!this.projectFormValid) return;
      try {
        const response = await api.post('/projects/', {
          name: this.projectForm.name.trim(),
          description: this.projectForm.description.trim(),
          client: this.projectForm.client,
        });
        const id = response.data?.id;
        this.closeProjectModal();
        if (id) {
          this.$router.push(`/projects/${id}/settings`);
        } else {
          await this.fetchProjects();
        }
      } catch (err) {
        this.projectsError = err.response?.data?.detail || err.message || 'Create failed';
      }
    },
  },
};
</script>

<style scoped>
.portfolio-hub {
  max-width: 1200px;
  margin: 0; /* top-left anchor — empty space stays on the right */
  padding-top: 0.5rem;
}

.hub-header {
  margin-bottom: 1.25rem;
}

.hub-header h1 {
  margin: 0;
  font-size: 1.75rem;
  color: var(--p-ink, #1e293b);
}

.hub-subtitle {
  margin: 0.35rem 0 0;
  color: var(--p-ink-muted, #64748b);
}

/* Diagonal bento: wide–narrow / narrow–wide (Projects gets the large cell) */
.p-bento--diagonal {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-areas:
    "a a b"
    "c d d";
}

.p-bento--diagonal > :nth-child(1) { grid-area: a; } /* Clients wide */
.p-bento--diagonal > :nth-child(2) { grid-area: b; } /* Client pulse */
.p-bento--diagonal > :nth-child(3) { grid-area: c; } /* Project pulse narrow */
.p-bento--diagonal > :nth-child(4) { grid-area: d; } /* Projects wide */

.p-bento-cell {
  background: var(--p-surface-elevated, #fff);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: 1rem;
  padding: 1.1rem 1.2rem;
  min-height: 14rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cell-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.cell-header-actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-shrink: 0;
}

.view-all-link {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--p-accent, #1e3a5f);
  text-decoration: none;
  white-space: nowrap;
}

.view-all-link:hover {
  text-decoration: underline;
}

.preview-overflow {
  margin: 0;
  font-size: 0.8rem;
  color: var(--p-ink-muted, #64748b);
}

.preview-overflow a {
  color: var(--p-accent, #1e3a5f);
  font-weight: 600;
}

.cell-header h2,
.pulse-cell h2 {
  margin: 0;
  font-size: 1.1rem;
  color: var(--p-ink, #1e293b);
}

.cell-sub {
  margin: 0.2rem 0 0;
  font-size: 0.8rem;
  color: var(--p-ink-muted, #64748b);
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.search-wrap {
  flex: 1;
  min-width: 10rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.65rem;
  border: 1px solid var(--p-border-strong, #cbd5e1);
  border-radius: 0.65rem;
  background: #fff;
}

.search-wrap input {
  border: none;
  outline: none;
  width: 100%;
  font: inherit;
}

.filter-select {
  padding: 0.45rem 0.5rem;
  border-radius: 0.65rem;
  border: 1px solid var(--p-border-strong, #cbd5e1);
  background: #fff;
}

.archive-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: var(--p-ink-muted, #64748b);
}

.entity-list {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow: auto;
  max-height: 28rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.entity-row {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0.75rem;
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: 0.75rem;
  background: var(--p-surface, #f8fafc);
}

.entity-row.muted {
  opacity: 0.7;
}

.entity-main {
  min-width: 0;
  flex: 1;
}

.entity-title {
  font-weight: 600;
  color: var(--p-ink, #1e293b);
  text-decoration: none;
}

.entity-desc {
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
  color: var(--p-ink-muted, #64748b);
}

.entity-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.4rem;
}

.entity-actions {
  display: flex;
  gap: 0.35rem;
  flex-shrink: 0;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background: #e2e8f0;
  color: #334155;
}

.pill-ok { background: #dcfce7; color: #166534; }
.pill-warn { background: #ffedd5; color: #9a3412; }
.pill-muted { background: #f1f5f9; color: #64748b; }

.pulse-cell {
  justify-content: flex-start;
}

.pulse-stat {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.5rem;
}

.pulse-stat.warn .pulse-value {
  color: #c2410c;
}

.pulse-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--p-accent, #1e3a5f);
  line-height: 1.1;
}

.pulse-label {
  font-size: 0.8rem;
  color: var(--p-ink-muted, #64748b);
}

.pulse-hint {
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: var(--p-ink-muted, #64748b);
}

.pulse-warn {
  border-color: color-mix(in srgb, #f59e0b 40%, #e2e8f0);
}

.cell-state {
  padding: 1rem 0;
  color: var(--p-ink-muted, #64748b);
  font-size: 0.9rem;
}

.cell-state.error {
  color: #b91c1c;
}

.linkish {
  background: none;
  border: none;
  color: var(--p-accent, #1e3a5f);
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-align: left;
  text-decoration: underline;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.75rem;
  border-radius: 0.65rem;
  border: 1px solid transparent;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  text-decoration: none;
}

.btn-sm { padding: 0.35rem 0.6rem; font-size: 0.8rem; }
.btn-block { width: 100%; justify-content: center; margin-top: 0.5rem; }

.btn-primary {
  background: var(--p-accent, #1e3a5f);
  color: #fff;
}

.btn-ghost {
  background: transparent;
  border-color: var(--p-border-strong, #cbd5e1);
  color: var(--p-ink, #1e293b);
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: #fff;
  border-radius: 1rem;
  width: 100%;
  max-width: 28rem;
  padding: 1.25rem;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.modal-header h3 { margin: 0; }

.close-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--p-ink-muted, #64748b);
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.modal-form label {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--p-ink, #1e293b);
}

.modal-form input,
.modal-form select,
.modal-form textarea {
  font: inherit;
  font-weight: 400;
  padding: 0.5rem 0.65rem;
  border-radius: 0.5rem;
  border: 1px solid var(--p-border-strong, #cbd5e1);
}

.form-hint {
  margin: 0;
  font-size: 0.8rem;
  color: var(--p-ink-muted, #64748b);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

@media (max-width: 900px) {
  .p-bento--diagonal {
    grid-template-columns: 1fr;
    grid-template-areas: none;
  }
  .p-bento--diagonal > :nth-child(n) {
    grid-area: auto;
  }
}
</style>
